import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { api } from '../../context/AuthContext';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  Upload, Play, Trash2, Loader2, Video, BookOpen, Plus, 
  Users, Hash, X,
  ChevronLeft, ChevronRight, Lock, Unlock,
  AlertTriangle, CheckCircle2, Eye, EyeOff, ToggleLeft, ToggleRight
} from 'lucide-react';
import { toast } from 'sonner';
import SimpleVideoPlayer from '../../components/SimpleVideoPlayer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
// ==================== Main Video Lectures Page ====================
const VideoLecturesPage = () => {
  const { user } = useAuth();
  const [lectures, setLectures] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [playingLecture, setPlayingLecture] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    title: '',
    sequence_number: '',
    description: '',
    share_with_all: false,
    shared_teacher_ids: []
  });
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [lecturesRes, teachersRes, classesRes] = await Promise.all([
        api.get('/video-lectures'),
        api.get('/users?role=teacher'),
        api.get('/classes')
      ]);
      setLectures(lecturesRes.data);
      setTeachers(teachersRes.data.filter(t => t.id !== user?.id));
      setClasses(classesRes.data || []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('video/')) {
      setSelectedFile(file);
    } else {
      toast.error('Please drop a valid video file');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) { toast.error('Please select a video file'); return; }
    if (!uploadForm.title) { toast.error('Please enter a title for this lecture'); return; }

    setUploading(true);
    setUploadProgress(0);
    
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('title', uploadForm.title);
    if (uploadForm.sequence_number) formData.append('sequence_number', uploadForm.sequence_number);
    if (uploadForm.description) formData.append('description', uploadForm.description);
    formData.append('share_with_all', uploadForm.share_with_all ? 'true' : 'false');
    formData.append('shared_teacher_ids', JSON.stringify(uploadForm.shared_teacher_ids));

    try {
      await api.post('/video-lectures', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded * 100) / (e.total || 1));
          setUploadProgress(pct);
        }
      });
      toast.success('Lecture uploaded successfully!');
      setUploadForm({ title: '', sequence_number: '', description: '', share_with_all: false, shared_teacher_ids: [] });
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [lockLoading, setLockLoading] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(null);
  const [classAssignLecture, setClassAssignLecture] = useState(null);
  const [classes, setClasses] = useState([]);

  const handleLock = async (lectureId) => {
    setLockLoading(lectureId);
    try {
      await api.put(`/video-lectures/${lectureId}/lock`);
      toast.success('Lecture locked');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to lock');
    } finally {
      setLockLoading(null);
    }
  };

  const handleUnlock = async (lectureId) => {
    setLockLoading(lectureId);
    try {
      await api.put(`/video-lectures/${lectureId}/unlock`);
      toast.success('Lecture unlocked');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlock');
    } finally {
      setLockLoading(null);
    }
  };

  const handleDelete = async (lectureId) => {
    try {
      await api.delete(`/video-lectures/${lectureId}`);
      toast.success('Lecture deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete lecture');
    }
  };

  const toggleTeacherShare = (teacherId) => {
    setUploadForm(prev => ({
      ...prev,
      shared_teacher_ids: prev.shared_teacher_ids.includes(teacherId)
        ? prev.shared_teacher_ids.filter(id => id !== teacherId)
        : [...prev.shared_teacher_ids, teacherId]
    }));
  };

  const handleToggleStudentAvailability = async (lecture) => {
    setAvailabilityLoading(lecture.id);
    try {
      const res = await api.put(`/video-lectures/${lecture.id}/toggle-student-availability`);
      toast.success(res.data.is_available_for_students ? 'Now visible to students' : 'Hidden from students');
      fetchData();
    } catch {
      toast.error('Failed to update availability');
    } finally {
      setAvailabilityLoading(null);
    }
  };

  const handleSaveClassAssignment = async (lectureId, classIds) => {
    try {
      await api.put(`/video-lectures/${lectureId}/classes`, { class_ids: classIds });
      toast.success('Class access updated');
      setClassAssignLecture(null);
      fetchData();
    } catch {
      toast.error('Failed to update class access');
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <DashboardLayout>
      {playingLecture && (
        <SimpleVideoPlayer
          videoSrc={playingLecture.video_path}
          title={playingLecture.title}
          subtitle={playingLecture.sequence_number ? `Lecture #${playingLecture.sequence_number}` : null}
          allowSeek={true}
          autoPlay={true}
          onVideoEnd={() => setPlayingLecture(null)}
          onClose={() => setPlayingLecture(null)}
        />
      )}
      
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Video className="h-7 w-7 text-indigo-600" />
            Video Lectures
          </h1>
          <p className="text-slate-500">Prepare and present video lectures for your students</p>
        </div>

        <Tabs defaultValue="prepare">
          <TabsList className="mb-4">
            <TabsTrigger value="prepare" className="flex items-center gap-2">
              <Plus className="h-4 w-4" /> Prepare Lecture
            </TabsTrigger>
            <TabsTrigger value="present" className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" /> Present Lecture
            </TabsTrigger>
          </TabsList>

          {/* ==================== PREPARE LECTURE TAB ==================== */}
          <TabsContent value="prepare">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Upload Form */}
              <Card>
                <CardHeader><CardTitle className="text-lg">Upload New Lecture</CardTitle></CardHeader>
                <CardContent>
                  <form onSubmit={handleUpload} className="space-y-4">
                    {/* Drag & Drop Zone */}
                    <div
                      onDragEnter={handleDrag} onDragOver={handleDrag}
                      onDragLeave={handleDrag} onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                        dragActive ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : 
                        selectedFile ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' :
                        'border-slate-300 dark:border-slate-700 hover:border-indigo-400'
                      }`}
                    >
                      <input ref={fileInputRef} type="file" accept="video/*" onChange={handleFileSelect} className="hidden" />
                      {selectedFile ? (
                        <div>
                          <Video className="h-10 w-10 mx-auto text-emerald-500 mb-2" />
                          <p className="font-medium text-emerald-700 dark:text-emerald-400">{selectedFile.name}</p>
                          <p className="text-sm text-slate-500">{formatSize(selectedFile.size)}</p>
                          <button type="button" onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                            className="mt-2 text-xs text-rose-500 hover:underline">Remove</button>
                        </div>
                      ) : (
                        <div>
                          <Upload className="h-10 w-10 mx-auto text-slate-400 mb-2" />
                          <p className="font-medium text-slate-600 dark:text-slate-400">Drag & Drop video here</p>
                          <p className="text-sm text-slate-400">or click to browse</p>
                          <p className="text-xs text-slate-400 mt-1">MP4, WebM, MKV, MOV, AVI and more</p>
                        </div>
                      )}
                    </div>

                    {uploading && (
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div className="bg-indigo-600 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                        <p className="text-center text-sm mt-1 text-slate-500">{uploadProgress}%</p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="title">Lecture Title *</Label>
                      <Input id="title" value={uploadForm.title}
                        onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                        placeholder="e.g., Chapter 1: Introduction to Grammar" />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="seqnum">Sequence Number (optional)</Label>
                      <Input id="seqnum" type="number" value={uploadForm.sequence_number}
                        onChange={(e) => setUploadForm({ ...uploadForm, sequence_number: e.target.value })}
                        placeholder="e.g., 1, 2, 3..." />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">Description (optional)</Label>
                      <Textarea id="description" rows={2} value={uploadForm.description}
                        onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                        placeholder="Brief description of this lecture" />
                    </div>

                    {/* Sharing Options */}
                    <div className="space-y-3 border rounded-lg p-4 bg-slate-50 dark:bg-slate-900">
                      <p className="font-medium text-sm">Sharing Options</p>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={uploadForm.share_with_all}
                          onChange={(e) => setUploadForm({ ...uploadForm, share_with_all: e.target.checked })}
                          className="rounded" />
                        <span className="text-sm">Make available to ALL teachers</span>
                      </label>
                      
                      {!uploadForm.share_with_all && teachers.length > 0 && (
                        <div>
                          <p className="text-sm text-slate-500 mb-2">Or share with specific teachers:</p>
                          <div className="flex flex-wrap gap-2">
                            {teachers.map(t => (
                              <label key={t.id} className={`flex items-center gap-1 px-2 py-1 rounded-full border text-xs cursor-pointer transition-all ${
                                uploadForm.shared_teacher_ids.includes(t.id) 
                                  ? 'bg-indigo-100 border-indigo-400 text-indigo-700' 
                                  : 'border-slate-200 hover:border-indigo-300'
                              }`}>
                                <input type="checkbox" checked={uploadForm.shared_teacher_ids.includes(t.id)}
                                  onChange={() => toggleTeacherShare(t.id)} className="sr-only" />
                                <Users className="h-3 w-3" /> {t.name}
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <Button type="submit" disabled={uploading || !selectedFile} className="w-full bg-indigo-600 hover:bg-indigo-700">
                      {uploading ? (
                        <><Loader2 className="h-4 w-4 animate-spin mr-2" />Uploading {uploadProgress}%</>
                      ) : (
                        <><Upload className="h-4 w-4 mr-2" />Upload Lecture</>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* My Uploaded Lectures */}
              <Card>
                <CardHeader><CardTitle className="text-lg">My Uploaded Lectures</CardTitle></CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>
                  ) : lectures.filter(l => l.is_own).length === 0 ? (
                    <div className="text-center py-8 text-slate-400">
                      <Video className="h-10 w-10 mx-auto mb-2" />
                      <p>No lectures uploaded yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {lectures.filter(l => l.is_own).map(lecture => (
                        <div key={lecture.id} className="flex items-start justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-indigo-300">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              {lecture.sequence_number && (
                                <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300">
                                  #{lecture.sequence_number}
                                </span>
                              )}
                              <p className="font-medium text-sm truncate">{lecture.title}</p>
                              {/* Student availability badge */}
                              {lecture.is_available_for_students ? (
                                <span className="text-xs px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 flex items-center gap-1">
                                  <Eye className="h-3 w-3" /> Students can view
                                </span>
                              ) : (
                                <span className="text-xs px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 flex items-center gap-1">
                                  <EyeOff className="h-3 w-3" /> Hidden from students
                                </span>
                              )}
                            </div>
                            {/* Assigned classes */}
                            {lecture.assigned_classes?.length > 0 && (
                              <p className="text-xs text-slate-400 mt-0.5">
                                Classes: {lecture.assigned_classes.map(c => c.name?.replace(/^Class\s+/i, '')).join(', ')}
                              </p>
                            )}
                            {/* Lock status */}
                            <div className="flex items-center gap-1 mt-1">
                              {lecture.is_locked ? (
                                <span className="text-xs text-amber-600 flex items-center gap-1">
                                  <Lock className="h-3 w-3" /> Locked
                                </span>
                              ) : (
                                <span className="text-xs text-emerald-600 flex items-center gap-1">
                                  <CheckCircle2 className="h-3 w-3" /> Active
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-400 mt-0.5">
                              {lecture.original_filename} · {formatSize(lecture.file_size)}
                              {lecture.share_with_all && ' · Shared with all'}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 ml-2 flex-wrap justify-end">
                            {/* Toggle student availability */}
                            <Button size="sm" variant="ghost"
                              className={`h-7 px-2 text-xs ${lecture.is_available_for_students ? 'text-emerald-600 hover:text-emerald-700' : 'text-slate-400 hover:text-emerald-600'}`}
                              onClick={() => handleToggleStudentAvailability(lecture)}
                              disabled={availabilityLoading === lecture.id}
                              title={lecture.is_available_for_students ? 'Hide from students' : 'Make available to students'}
                            >
                              {availabilityLoading === lecture.id ? <Loader2 className="h-3 w-3 animate-spin" /> :
                                lecture.is_available_for_students
                                  ? <><ToggleRight className="h-4 w-4 mr-1" />On</>
                                  : <><ToggleLeft className="h-4 w-4 mr-1" />Off</>
                              }
                            </Button>
                            {/* Assign classes */}
                            {lecture.is_available_for_students && (
                              <Button size="sm" variant="ghost"
                                className="h-7 px-2 text-xs text-blue-600 hover:text-blue-700"
                                onClick={() => setClassAssignLecture({ lecture, selected_class_ids: lecture.assigned_classes?.map(c => c.id) || [] })}
                                title="Assign to classes"
                              >
                                <Users className="h-3.5 w-3.5 mr-1" />Classes
                              </Button>
                            )}
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-indigo-600"
                              onClick={() => setPlayingLecture(lecture)} title="Play">
                              <Play className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost"
                                className={`h-7 w-7 ${lecture.is_locked ? 'text-amber-500' : 'text-slate-400 hover:text-amber-500'}`}
                                onClick={() => lecture.is_locked ? handleUnlock(lecture.id) : handleLock(lecture.id)}
                                disabled={lockLoading === lecture.id}
                                title={lecture.is_locked ? 'Unlock' : 'Lock'}
                              >
                                {lockLoading === lecture.id ? <Loader2 className="h-3 w-3 animate-spin" /> :
                                  lecture.is_locked ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />
                                }
                              </Button>}
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-500"
                              onClick={() => setDeleteConfirm(lecture.id)} title="Delete">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ==================== PRESENT LECTURE TAB ==================== */}
          <TabsContent value="present">
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>
            ) : lectures.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Video className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No lectures available</p>
                  <p className="text-sm text-slate-400">Upload a lecture in the "Prepare Lecture" tab first</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {lectures.map(lecture => (
                  <Card key={lecture.id} className="group hover:shadow-lg transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="inline-flex p-2.5 rounded-xl bg-indigo-100 dark:bg-indigo-900/30">
                          <Video className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        {lecture.sequence_number && (
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                            Lecture #{lecture.sequence_number}
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-slate-900 dark:text-white mb-1">{lecture.title}</h3>
                      {lecture.description && (
                        <p className="text-xs text-slate-500 mb-2 line-clamp-2">{lecture.description}</p>
                      )}
                      <p className="text-xs text-slate-400 mb-4">
                        By {lecture.creator_name} · {formatSize(lecture.file_size)}
                      </p>
                      <Button 
                        className="w-full bg-indigo-600 hover:bg-indigo-700 gap-2"
                        onClick={() => setPlayingLecture(lecture)}
                      >
                        <Play className="h-4 w-4" /> Play Lecture
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            <div className="mt-4 p-3 rounded-lg bg-slate-100 dark:bg-slate-800 text-xs text-slate-500 text-center">
              Keyboard shortcuts: Space=Play/Pause | ← →=Skip 10s | ↑ ↓=Volume | F=Fullscreen
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-sm w-full shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="h-6 w-6 text-amber-500" />
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Delete Lecture</h3>
            </div>
            <p className="text-slate-600 dark:text-slate-300 text-sm mb-6">
              Are you sure? This will permanently delete the video file. The lecture will be removed from students' view.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
              <Button variant="destructive" className="flex-1" onClick={() => { handleDelete(deleteConfirm); setDeleteConfirm(null); }}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Class Assignment Dialog */}
      {classAssignLecture && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-lg w-full shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">Assign Classes</h3>
            <p className="text-sm text-slate-500 mb-4">Select which classes can access "<strong>{classAssignLecture.lecture.title}</strong>"</p>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 max-h-64 overflow-y-auto mb-4">
              {classes.map(cls => {
                const isSelected = classAssignLecture.selected_class_ids.includes(cls.id);
                return (
                  <button key={cls.id}
                    onClick={() => {
                      const ids = classAssignLecture.selected_class_ids;
                      setClassAssignLecture(prev => ({
                        ...prev,
                        selected_class_ids: isSelected ? ids.filter(id => id !== cls.id) : [...ids, cls.id]
                      }));
                    }}
                    className={`p-2 rounded-lg border-2 text-sm font-medium transition-all ${isSelected ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : 'border-slate-200 dark:border-slate-700 hover:border-indigo-300'}`}
                  >
                    {cls.name?.replace(/^Class\s+/i, '') || cls.name}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setClassAssignLecture(null)}>Cancel</Button>
              <Button className="flex-1" onClick={() => handleSaveClassAssignment(classAssignLecture.lecture.id, classAssignLecture.selected_class_ids)}>
                Save Access
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default VideoLecturesPage;
